import React, { useContext, useState } from 'react';
import {
  Outlet,
} from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { tss } from 'tss-react';
import {
  extendTheme as materialExtendTheme,
  Experimental_CssVarsProvider as MaterialCssVarsProvider,
  THEME_ID as MATERIAL_THEME_ID,
} from '@mui/material/styles';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LicenseInfo } from '@mui/x-license-pro';
import { CssVarsProvider, Theme, useTheme } from '@mui/joy/styles';
import CssBaseline from '@mui/joy/CssBaseline';
import posthog from 'posthog-js';

import {
  Button,
  Card,
} from '@mui/joy';
import { getAPI } from 'src/api';
import joyTheme from '../theme';
import Tracking from '../components/Tracking';
import { ErrorView } from '../components/ErrorBoundary';
import constants from '../constants';
import { assetLink } from '../utils';
import { ListingAuthContext } from './ListingAuthContext';
import { LogInModal } from './components/LogInModal';
import { SettingsModal } from './components/SettingsModal';

const drawerHeight = 72;
const useStyles = tss
  .withParams<{ theme: Theme }>()
  .create(({ theme }) => ({
    container: {
      display: 'flex',
      flexDirection: 'row',
      height: '100vh',
      width: '100vw',
    },
    drawer: {
      borderRadius: 0,
      position: 'fixed',
      width: '100%',
      height: drawerHeight,
      zIndex: theme.zIndex.table,
      display: 'flex',
      flexDirection: 'row',
      boxShadow: theme.shadow.sm,
      padding: theme.spacing(1),
      alignItems: 'center',
    },
    title: {
      marginLeft: theme.spacing(2),
      cursor: 'pointer',
    },
    itemIcon: {
      width: 28,
      height: 28,
    },
    unselectedColor: {
      color: theme.palette.text.secondary,
    },
    selectedColor: {
      color: theme.palette.primary.softColor,
    },
    item: {
      borderRadius: theme.spacing(1),
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    content: {
      position: 'absolute',
      top: drawerHeight,
      width: '100%',
      height: `calc(100% - ${drawerHeight}px)`,
      overflowY: 'auto',
    },
    links: {
      display: 'flex',
      flexDirection: 'row',
      marginLeft: 'auto',
      gap: theme.spacing(1),
    },
  }));

LicenseInfo.setLicenseKey(constants.muiProLicense);

const materialTheme = materialExtendTheme();

export function Layout() {
  const { user } = useContext(ListingAuthContext);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const theme = useTheme();
  const { classes } = useStyles({ theme });
  const api = getAPI();

  const logout = async () => {
    await api.listings.logout();
    posthog.reset();
    window.location.reload();
  };

  return (
    <MaterialCssVarsProvider theme={{ [MATERIAL_THEME_ID]: materialTheme }}>
      <CssVarsProvider theme={joyTheme}>
        <Tracking>
          <CssBaseline />
          <div className={classes.container}>
            <Card className={classes.drawer}>
              <img
                className={classes.title}
                src={assetLink('logo.svg')}
                width={132}
                height={28}
                alt='logo'
              />
              <div className={classes.links}>
                {
                  !user && (
                    <Button
                      variant="outlined"
                      size="lg"
                      onClick={() => setShowLoginModal(true)}
                    >
                      Log In
                    </Button>
                  )
                }
                {
                  user &&
                  (
                    <Button
                      variant="outlined"
                      size="lg"
                      onClick={() => setShowSettingsModal(true)}
                    >
                      Profile
                    </Button>
                  )
                }
                {
                  user &&
                  (
                    <Button
                      size="lg"
                      onClick={logout}
                    >
                      Log Out
                    </Button>
                  )
                }
              </div>
            </Card>
            <div className={classes.content}>
              <ErrorBoundary FallbackComponent={ErrorView}>
                <Outlet />
                {
                  showLoginModal && (
                    <LogInModal
                      onClose={() => setShowLoginModal(false)}
                    />
                  )
                }
                {
                  showSettingsModal && (
                    <SettingsModal
                      onClose={() => setShowSettingsModal(false)} />
                  )
                }
              </ErrorBoundary>
            </div>
          </div>
        </Tracking>
      </CssVarsProvider>
    </MaterialCssVarsProvider>
  );
}
